import PropTypes from 'prop-types';
import React from 'react';
import { Col, ControlLabel, Panel, PanelGroup, Row, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { attachmentFormatter, boolFormatter, dateTimeFormatter, dateTimeUTCFormatter, extractContentFromHTML, objectFormatter, patientVisitStatusFormatter, timeFormatter, visitStatusFormatter, yesNoFormatter, yesNoNotApplyFormatter, dateFieldFormatter, protocolStatusFormatter } from '../../components/Utils/Formatter';
import { HighLight } from '../CustomControls/CustomTextStyle';
import isequal from "lodash.isequal";
import { decodeFileName } from "../Utils/Commons";
import { resolveArrayItemFieldType, resolveNewArrayItemName } from "./arrayFieldAuditRows";
import { isInventoryFieldType, resolveInventoryAuditLines } from "./inventoryAuditRows";

export const Audit = ({ medicalRecordNumber, audit, fields }) => {
    const { t } = useTranslation();

    const _getActionName = (actionCode) => {
        switch (actionCode) {
            case 0: return t('auditTrail.actions.detached');
            case 1: return t('auditTrail.actions.unchanged');
            case 2: return t('auditTrail.actions.deleted');
            case 3: return t('auditTrail.actions.modified');
            case 4: return t('auditTrail.actions.added');
            default:
                return t('auditTrail.actions.detached');
        }
    }

    const _getLocaleSuffix = (entity) => {
        switch (entity) {
            case "Protocol": return "protocols.brief.";
            case "ProtocolPractitioner": return "protocols.practitioner.";
            case "VisitTrackingForm":
            case "VisitTrackingExtra": return "protocols.visitTrackingFields.";
            case "Entry": return "medicalRecords.";
            case "AdverseEvents":
            case "Drugs":
            case "Procedures":
            case "Vitals":
            case "Anthropometrics":
            case "FamilyHistory":
            case "PersonalHistory":
            case "PersonalHabits":
            case "LaboratoryResult":
            case "Prescriptions":
            case "PrescriptionsItems":
            case "Orders":
            case "OrdersItems":
            case "All":
                return "medicalRecords";
            default:
                return "";
        }
    }

    const _uncapitalize = (text) => {
        return text.toString().charAt(0).toLowerCase() + text.toString().slice(1);
    }

    const _uncapitalizeAll = (text) => {
        return text.replace(/^([A-Z])|(\.[A-Z])/g, function (_, g1, g2) {
            return (typeof g1 === 'undefined') ? g2.toLowerCase() : g1.toLowerCase();
        });
    }

    // Convierte strings booleanos a "activo"/"inactivo" o "Si"/"No" según el contexto
    const _formatBooleanString = (value) => {
        if (value === null || value === undefined) {
            return value;
        }
        if (typeof value === "string") {
            // Primero verificar si el valor completo es "true" o "false" (para activo/inactivo)
            const normalized = value.trim().toLowerCase();
            if (normalized === "true") {
                return "si";
            } else if (normalized === "false") {
                return "no";
            }

            // Si no es el valor completo, buscar "True" o "False" en cualquier parte del string
            // y reemplazarlos por "Si" y "No" respectivamente
            let formatted = value;

            // Reemplazar "True" por "Si" (palabra completa, case-sensitive)
            formatted = formatted.replace(/\bTrue\b/g, "Si");
            // Reemplazar "False" por "No" (palabra completa, case-sensitive)
            formatted = formatted.replace(/\bFalse\b/g, "No");
            // También manejar minúsculas por si acaso
            formatted = formatted.replace(/\btrue\b/g, "si");
            formatted = formatted.replace(/\bfalse\b/g, "no");

            return formatted;
        }
        return value;
    };

    const _renderInventoryAuditLine = (line, index) => {
        const quantity = <>{t('inventory.dispense.quantity')}: {line.quantity}</>;

        return (
            <div key={`${index}-inventory-line-${line.key}`}>
                <HighLight>{t(`inventory.movements.moveType.${line.moveType}`)}</HighLight>: {line.label}
                {line.lotNumber ? <> {t('inventory.commons.lotNumber')}: {line.lotNumber}</> : null}
                {line.kitNumber ? <> {t('inventory.commons.kitNumber')}: {line.kitNumber}</> : null}
                {' '}
                {line.status === 'same' ? quantity : <HighLight>{quantity}</HighLight>}
            </div>
        );
    }

    const _getInventoryFieldName = (row) => {
        const [line] = resolveInventoryAuditLines(row.current ?? row.before, null);
        return line ? t(`inventory.movements.moveType.${line.moveType}`) : row.fieldName;
    }

    const _getValueFormatted = (item, value, index) => {

        function getDefaultValue(value) {
            if (value !== null && value !== undefined) {
                if (value instanceof Object)
                    return objectFormatter(value);
                else
                    return extractContentFromHTML(value.toString())
            }

            return "";
        }

        //        console.log(`item: ${JSON.stringify(item)} value: ${value}`)
        if (item._t) {
            switch (item._t?.toLowerCase()) {
                case "timefield":
                    return timeFormatter(value);
                case "datetimefield":
                case "modified":
                case "datetime":
                    return dateTimeFormatter(value);
                case "datefield":
                    return dateFieldFormatter(value);
                case "yesnofield":
                    return yesNoNotApplyFormatter(value, t);
                case "yesno":
                    return yesNoFormatter(value, t);
                case "payablefield":
                case "booleanfield":
                case "checkboxfield":
                    return boolFormatter(value, t);
                case "selectasyncfield":
                    return objectFormatter(value);
                case "userfield":
                    if (value instanceof Object)
                        return value.fullNameUnique;
                    else
                        return value;
                case "array": {
                    return value?.map((element, idx) => {
                        let rows = [];
                        for (var propertyName in element) {
                            if (
                                !element[propertyName] ||
                                propertyName.includes("ToYear") ||
                                propertyName.includes("ToMonth") ||
                                propertyName.includes("ToDay") ||
                                propertyName.includes("FromYear") ||
                                propertyName.includes("FromMonth") ||
                                propertyName.includes("FromDay") ||
                                propertyName.includes("EntryId") ||
                                propertyName.includes("Created") ||
                                propertyName.includes("CreatedBy")
                            )
                                continue;

                            var elementValue = "";
                            if (element[propertyName] instanceof Object) {
                                elementValue = objectFormatter(element[propertyName]);
                            } else {
                                elementValue = element[propertyName];
                            }
                            rows.push(
                                <div key={`${index}-array-row-${idx}-${propertyName}`}>
                                    {t(`${item.localeSuffix}.${_uncapitalize(propertyName)}`)}:
                                    {elementValue}
                                </div>
                            );
                        }

                        return rows;
                    });
                }
                case "arrayfield": {
                    if (!Array.isArray(value)) return value;

                    return value?.map((component, idx) => {
                        let rows = [];
                        component.Fields.forEach((field, childIdx) => {
                            var elementValue = _getValueFormatted(field, field["Value"], "recursive");

                            rows.push(
                                <div key={`${index}-array-field-row-${idx}-${childIdx}`}>
                                    {field["Label"]}:{elementValue}
                                </div>
                            );
                        });

                        return rows;
                    })
                }
                case "arrayofstrings": {
                    if (!value || (Array.isArray(value) && value.length === 0)) {
                        return <div>-</div>;
                    }
                    return value?.map((element, idx) => {
                        const displayValue =
                            typeof element === "string" ? decodeFileName(element) : element;
                        const formattedElement = _formatBooleanString(displayValue);
                        return (
                            <div key={`${index}-array-row-${idx}-${element}`}>
                                {formattedElement}
                            </div>
                        );
                    });
                }
                case "adverseeventsopenitemsfield":
                case "personalhistoryopenitemsfield":
                case "drugsopenitemsfield":
                case "proceduresopenitemsfield": {
                    if (!Array.isArray(value))
                        return value?.toString();

                    const rows = value?.map((item, idx) => {
                        return <div key={`${index}-openitem-field-row-${idx}`}>
                            {item.Name}
                            :
                            {`${t(`medicalRecords.to`)} ${item.Closed.ToDay ?? "UNK"}/${(item.Closed.ToMonth ?? "UNK") + 1}/${item.Closed.ToYear}`}
                        </div>
                    })
                    return rows;
                }
                case "procedures":
                case "drugs":
                case "adverseevents":
                    if ((typeof value === 'string' || value instanceof String) && value.includes("/")) // Si el valor es date (SIMIADA, lo se)
                        return dateFieldFormatter(value);
                    else return getDefaultValue(value);
                case "fileuploadfield": {
                    if (value && item?.propertyName?.includes(".ValidatedBy"))
                        return t("protocols.visit.formTemplate.label_fileUploadField");

                    if (value instanceof Object)
                        return attachmentFormatter(value);
                    else
                        return typeof value === "string"
                            ? decodeFileName(value)
                            : value?.toString();
                }
                case "pharmacyformfield":
                case "protocolformfield":
                case "protocolimpformfield":
                case "protocolconcomitantformfield": {
                    const counterpart = value === item.before ? item.current : item.before;
                    const lines = resolveInventoryAuditLines(value, counterpart);

                    if (lines.length === 0)
                        return getDefaultValue(value);

                    return lines.map(line => _renderInventoryAuditLine(line, index));
                }
                default: {
                    return getDefaultValue(value);
                }
            }
        }

        if (item.propertyName) {
            switch (item.propertyName) {
                case "Status":
                    // Para Startup/Protocol, usar el formateador de estado de protocolo
                    if (
                        item.entityName?.toLowerCase() === "startup" ||
                        item.entityName?.toLowerCase() === "protocol"
                    ) {
                        return protocolStatusFormatter(value);
                    }
                    return visitStatusFormatter(value);
                case "PatientStatus":
                    return patientVisitStatusFormatter(value);
                default: {
                    if (value !== null && value !== undefined) {
                        if (value instanceof Object) return objectFormatter(value);
                        else {
                            // Para Startup, formatear booleanos a "si"/"no"
                            const strValue = extractContentFromHTML(value.toString());
                            if (item.entityName?.toLowerCase() === "startup") {
                                return _formatBooleanString(strValue);
                            }
                            return strValue;
                        }
                    }

                    return "";
                }
            }
        }
    }

    const _getFieldTypeFromName = (propertyName) => {
        // Campos que deben mostrar solo fecha (sin hora)
        const dateOnlyFields = [
            "PreselectionDate",
            "SelectionDate",
            "VisitReceptionDate",
            "ScheduledVisitStartDate",
            "CenterActivationDate",
            "StartOfTask",
            "EndOfTask",
        ];

        if (dateOnlyFields.includes(propertyName)) {
            return "DateField";
        }

        if (propertyName.endsWith("Date")) {
            return "DateTimeField";
        }
        switch (propertyName) {
            case "Created":
            case "Modified":
                return "DateTimeField";
            default:
                return null;
        }
    };

    const _getFieldName = (entityName, propertyName) => {
        switch (entityName) {
            case "Device":
                return t(`devices.${_uncapitalizeAll(propertyName)}`);
            case "Protocol":
                return t(`protocols.brief.${_uncapitalizeAll(propertyName)}`);
            case "ProtocolPractitioner":
                return t(`protocols.practitioner.${_uncapitalizeAll(propertyName)}`);
            case "VisitTracking":
                return t(
                    `protocols.visitTrackingFields.${_uncapitalizeAll(propertyName)}`
                );
            case "VisitTrackingExtra":
                return t(
                    `protocols.visitTrackingFields.${_uncapitalizeAll(propertyName)}`
                );
            case "Startup":
                // Title = Acrónimo, FullTitle = Título (Identificación)
                if (propertyName === "Title") return t("clinicalTrials.startup.acronym");
                if (propertyName === "FullTitle") return t("clinicalTrials.startup.title");
                return t(
                    `clinicalTrials.startup.${_uncapitalizeAll(propertyName)}`
                );
            default:
                return t(
                    `${_getLocaleSuffix(entityName)}.${_uncapitalizeAll(
                        entityName
                    )}.${_uncapitalizeAll(propertyName)}`
                );
        }
    }

    const Reason = ({ reason }) => {
        if (!reason || reason === "")
            return '';

        return (
            <Row>
                <Col sm={3}>
                    <ControlLabel>
                        {t('auditTrail.reason')}
                    </ControlLabel>
                </Col>
                <Col sm={9}>
                    {t(`auditTrail.reasons.${reason}`)}
                </Col>
            </Row>
        );
    }

    const Comments = ({ comments }) => {
        if (!comments || comments === "")
            return '';

        return (
            <Row>
                <Col sm={3}>
                    <ControlLabel>
                        {t('auditTrail.comments')}
                    </ControlLabel>
                </Col>
                <Col sm={9}>
                    {comments}
                </Col>
            </Row>);
    }

    const Visit = ({ visitId }) => {
        if (!visitId || visitId === 0 || !medicalRecordNumber)
            return null;

        const url = medicalRecordNumber && `/admin/medicalRecords/${medicalRecordNumber}/entry/${visitId}`;
        return (
            <Row>
                <Col sm={12}>
                    <ControlLabel>
                        <NavLink to={url} activeClassName="active">
                            {t("medicalRecords.viewVisit")}
                        </NavLink>
                    </ControlLabel>
                </Col>
            </Row>);
    }

    const _getFieldNameType = (row, field, propertyName) => {
        const indexes = [...propertyName.matchAll(/\d+/g)];
        if (propertyName.includes(".Components[")) { // Updated item in arrayField. 
            // El ítem auditado puede haberse eliminado después del cambio.
            const component = field.components?.[indexes[1]];
            const componentField = component?.fields?.[indexes[2]];
            row.fieldName = field.componentsType === "LaboratoryResults" ? component?.fields?.[0]?.value?.description : componentField?.label;
            row._t = resolveArrayItemFieldType(field, componentField);
        }
        else
            if (propertyName.includes("].Components")) { // New item in ArrayField
                row.fieldName = resolveNewArrayItemName(field, t);
                row._t = field._t;
            }
            else {
                row.fieldName = field.label;
                row._t = field._t;
            }
    }

    const _getFieldVisitTrackingFormMetadata = (row, propertyName) => {
        const indexes = [...propertyName.matchAll(/\d+/g)];
        if (!indexes || indexes.length === 0) return;

        const field = fields[indexes[0]];
        if (!field) return;

        switch (field._t) {
            case "AdverseEventsOpenItemsField":
            case "PersonalHistoryOpenItemsField":
            case "DrugsOpenItemsField":
            case "ProceduresOpenItemsField":
                if (!field.value[indexes[1]]) {
                    row.fieldName = "Item Cerrado";
                } else
                    row.fieldName = `${field.value[indexes[1]].name}: ${_getFieldName("VisitTracking", propertyName.slice(propertyName.lastIndexOf(".") + 1))}`;
                row._t = field._t;
                return;
            default:
                _getFieldNameType(row, field, propertyName);
                return;
        }
    }

    const auditItem = audit?.map((item, idx) => {
        const oldValues = item.oldValues;
        const currentValues = item.newValues;

        var rows = [];

        // Si se borró un item, el "for" lo hago desde oldValues.
        const values = isequal(item.newValues, {})
            ? item.oldValues
            : item.newValues;

        // Agrupar índices individuales de arrays (ej: AttachmentFieldValues[2]) en arrays completos
        // si el array completo no existe
        const groupedValues = { ...values };
        const groupedOldValues = { ...oldValues };
        const groupedCurrentValues = { ...currentValues };

        // Buscar propiedades que son índices de arrays (ej: AttachmentFieldValues[2])
        const arrayIndexPattern = /^([A-Za-z]+)\[(\d+)\]$/;
        const arrayIndices = {};

        for (var propName in values) {
            const match = propName.match(arrayIndexPattern);
            if (match) {
                const arrayName = match[1];

                // Si el array completo no existe, agrupar los índices
                if (
                    !groupedValues[arrayName] ||
                    !Array.isArray(groupedValues[arrayName])
                ) {
                    if (!arrayIndices[arrayName]) {
                        arrayIndices[arrayName] = { old: [], current: [] };
                    }

                    // Agregar el valor al array (no importa el orden para auditoría)
                    if (
                        oldValues[propName] !== undefined &&
                        oldValues[propName] !== null
                    ) {
                        arrayIndices[arrayName].old.push(oldValues[propName]);
                    }
                    if (
                        currentValues[propName] !== undefined &&
                        currentValues[propName] !== null
                    ) {
                        arrayIndices[arrayName].current.push(currentValues[propName]);
                    }
                }
            }
        }

        // Agregar los arrays agrupados a los valores
        for (var arrayName in arrayIndices) {
            groupedOldValues[arrayName] = arrayIndices[arrayName].old;
            groupedCurrentValues[arrayName] = arrayIndices[arrayName].current;
            groupedValues[arrayName] = groupedCurrentValues[arrayName];
        }

        // Identificar qué categorías de documentos cambiaron (para filtrar AttachmentFieldValues)
        // Esto se hace ANTES del loop principal para poder usar la información después
        let changedCategories = new Set();
        if (item.entityName.toLowerCase() === "startup") {
            const parseAttachment = (item) => {
                if (typeof item !== "string") return null;
                const colonIndex = item.indexOf(":");
                if (colonIndex > 0) {
                    return item.substring(0, colonIndex).trim();
                }
                return item.trim();
            };

            const beforeAttachments = Array.isArray(groupedOldValues["Attachments"])
                ? groupedOldValues["Attachments"]
                : [];
            const currentAttachments = Array.isArray(
                groupedCurrentValues["Attachments"]
            )
                ? groupedCurrentValues["Attachments"]
                : [];

            const beforeCategories = beforeAttachments
                .map(parseAttachment)
                .filter(Boolean);
            const currentCategories = currentAttachments
                .map(parseAttachment)
                .filter(Boolean);

            // Encontrar categorías que cambiaron (agregadas, eliminadas o modificadas)
            const allCategories = new Set([
                ...beforeCategories,
                ...currentCategories,
            ]);
            for (const category of allCategories) {
                const wasInBefore = beforeCategories.includes(category);
                const isInCurrent = currentCategories.includes(category);

                // Si cambió (agregado, eliminado o modificado)
                if (wasInBefore !== isInCurrent) {
                    changedCategories.add(category);
                } else if (wasInBefore && isInCurrent) {
                    // Verificar si el archivo cambió (mismo nombre de categoría pero diferente archivo)
                    const beforeFile = beforeAttachments.find(
                        (a) => parseAttachment(a) === category
                    );
                    const currentFile = currentAttachments.find(
                        (a) => parseAttachment(a) === category
                    );
                    if (beforeFile !== currentFile) {
                        changedCategories.add(category);
                    }
                }
            }
        }

        for (var propertyName in groupedValues) {
            // Si es un índice de array (ej: Attachments[0]) y ya existe el array agrupado, saltarlo
            const arrayIndexMatch = propertyName.match(/^([A-Za-z]+)\[\d+\]$/);
            if (arrayIndexMatch && groupedValues[arrayIndexMatch[1]]) {
                continue;
            }

            var row = {
                propertyName,
                before: groupedOldValues[propertyName] ?? oldValues[propertyName],
                current:
                    groupedCurrentValues[propertyName] ?? currentValues[propertyName],
                entityName: item.entityName,
            };

            try {
                // Ignoro campos internos.
                if (
                    propertyName.includes(".Id") ||
                    propertyName.includes("Id") ||
                    propertyName.includes(".ConceptId") ||
                    propertyName.includes(".FileExtension") ||
                    propertyName.includes(".Path") ||
                    propertyName.includes(".Code") ||
                    propertyName.includes("ToYear") ||
                    propertyName.includes("ToMonth") ||
                    propertyName.includes("ToDay") ||
                    propertyName.includes("FromYear") ||
                    propertyName.includes("FromMonth") ||
                    propertyName.includes("FromDay") ||
                    propertyName.includes("DiagnosisYear") ||
                    propertyName.includes("DiagnosisMonth") ||
                    propertyName.includes("DiagnosisDay") ||
                    propertyName.includes("CreatedBy") ||
                    propertyName.includes("Category") || 
                    propertyName.includes("Version") || 
                    propertyName.includes("IsCurrent") ||
                    propertyName.includes("Code")
                )
                    continue;

                switch (item.entityName.toLowerCase()) {
                    case "visittrackingform":
                    case "visittrackingextraform":
                        _getFieldVisitTrackingFormMetadata(row, propertyName);
                        if (!row.fieldName && isInventoryFieldType(row._t))
                            row.fieldName = _getInventoryFieldName(row);
                        break;
                    case "startup":
                        {
                            if (Array.isArray(row.current)) {
                                // Para startup, filtramos duplicados en arrays para mostrar solo diferencias
                                const filterArrayDifferences = (beforeArr, currentArr) => {
                                    if (!Array.isArray(beforeArr)) beforeArr = [];
                                    if (!Array.isArray(currentArr)) currentArr = [];

                                    // Normalizamos los arrays para comparación (trim y normalización)
                                    const normalizeItem = (item) => {
                                        if (typeof item === "string") {
                                            return item.trim();
                                        }
                                        return item;
                                    };

                                    const beforeNormalized = beforeArr.map(normalizeItem);
                                    const currentNormalized = currentArr.map(normalizeItem);

                                    // Si no había documentos antes (primera vez que se agregan)
                                    if (beforeNormalized.length === 0) {
                                        // ANTES: vacío
                                        // DESPUÉS: todos los documentos que quedaron después (es la primera vez)
                                        return {
                                            before: [],
                                            current: currentNormalized,
                                            hasChanges: currentNormalized.length > 0,
                                        };
                                    }

                                    // Si ya había documentos antes (modificación posterior)
                                    // Encontramos items que solo están en "after" (nuevos agregados en este cambio)
                                    const onlyInAfter = currentNormalized.filter(
                                        (item) => !beforeNormalized.includes(item)
                                    );

                                    // Encontramos items que solo están en "before" (eliminados en este cambio)
                                    const onlyInBefore = beforeNormalized.filter(
                                        (item) => !currentNormalized.includes(item)
                                    );

                                    // Si solo hay elementos eliminados (no hay elementos nuevos)
                                    if (onlyInBefore.length > 0 && onlyInAfter.length === 0) {
                                        // ANTES: los documentos eliminados
                                        // DESPUÉS: vacío
                                        return {
                                            before: onlyInBefore,
                                            current: [],
                                            hasChanges: true,
                                        };
                                    }

                                    // Si hay elementos nuevos (puede haber también eliminados, pero mostramos solo los nuevos)
                                    // ANTES: todos los documentos que existían antes de esta modificación
                                    // DESPUÉS: solo los documentos nuevos agregados en este cambio específico
                                    return {
                                        before: beforeNormalized,
                                        current: onlyInAfter,
                                        hasChanges:
                                            onlyInAfter.length > 0 || onlyInBefore.length > 0,
                                    };
                                };

                                if (propertyName.toLowerCase() === "attachmentfieldvalues") {
                                    // Lógica para AttachmentFieldValues (formato especial con metadata)
                                    // Solo mostrar campos del documento que realmente cambió
                                    const filterAttachmentFieldValues = (
                                        beforeArr,
                                        currentArr
                                    ) => {
                                        // Asegurar que sean arrays
                                        if (!Array.isArray(beforeArr)) {
                                            beforeArr = beforeArr != null ? [beforeArr] : [];
                                        }
                                        if (!Array.isArray(currentArr)) {
                                            currentArr = currentArr != null ? [currentArr] : [];
                                        }

                                        // Normalizamos para comparación
                                        const normalizeItem = (item) => {
                                            if (typeof item === "string") {
                                                return item.trim();
                                            }
                                            return item;
                                        };

                                        const beforeNormalized = beforeArr.map(normalizeItem);
                                        const currentNormalized = currentArr.map(normalizeItem);

                                        // Extraemos solo el nombre del documento (antes del paréntesis)
                                        const getDocumentName = (item) => {
                                            if (typeof item === "string") {
                                                const parenIndex = item.indexOf("(");
                                                if (parenIndex > 0) {
                                                    return decodeFileName(
                                                        item.substring(0, parenIndex).trim()
                                                    );
                                                }
                                                return decodeFileName(item.trim());
                                            }
                                            return item;
                                        };

                                        // Extraemos el identificador completo del campo (todo antes del ":")
                                        // Esto incluye el nombre del documento Y el nombre del campo específico
                                        // Ej: "CDA (Confidential Disclosure Agreement) (¿Enviado?): True" → "CDA (Confidential Disclosure Agreement) (¿Enviado?)"
                                        const getFieldIdentifier = (item) => {
                                            if (typeof item === "string") {
                                                const colonIndex = item.indexOf(":");
                                                if (colonIndex > 0) {
                                                    return item.substring(0, colonIndex).trim();
                                                }
                                                return item.trim();
                                            }
                                            return item;
                                        };

                                        // Si no hay categorías identificadas desde Attachments, identificar desde los campos que cambiaron
                                        if (changedCategories.size === 0) {
                                            // Identificar categorías solo de los campos que realmente cambiaron
                                            const beforeMap = new Map();
                                            beforeNormalized.forEach((item) => {
                                                const docName = getDocumentName(item);
                                                if (docName) {
                                                    beforeMap.set(item, docName);
                                                }
                                            });

                                            currentNormalized.forEach((currentItem) => {
                                                const foundInBefore = beforeNormalized.find(
                                                    (beforeItem) => beforeItem === currentItem
                                                );
                                                if (!foundInBefore) {
                                                    // Es un campo nuevo o modificado
                                                    const docName = getDocumentName(currentItem);
                                                    if (docName) {
                                                        changedCategories.add(docName);
                                                    }
                                                }
                                            });

                                            // También verificar campos que fueron eliminados
                                            beforeNormalized.forEach((beforeItem) => {
                                                const foundInCurrent = currentNormalized.find(
                                                    (currentItem) => currentItem === beforeItem
                                                );
                                                if (!foundInCurrent) {
                                                    // El campo fue eliminado o modificado
                                                    const docName = getDocumentName(beforeItem);
                                                    if (docName) {
                                                        changedCategories.add(docName);
                                                    }
                                                }
                                            });
                                        }

                                        // Filtrar campos que pertenecen solo a las categorías que cambiaron
                                        const filterByChangedCategories = (arr) => {
                                            if (changedCategories.size === 0) {
                                                // Si no hay categorías, mostrar todos los campos (compatibilidad)
                                                return arr;
                                            }
                                            return arr.filter((item) => {
                                                const docName = getDocumentName(item);
                                                return changedCategories.has(docName);
                                            });
                                        };

                                        const beforeFiltered =
                                            filterByChangedCategories(beforeNormalized);
                                        const currentFiltered =
                                            filterByChangedCategories(currentNormalized);

                                        // Si no había campos antes (primera vez que se agregan para estas categorías)
                                        if (beforeFiltered.length === 0) {
                                            // ANTES: vacío
                                            // DESPUÉS: todos los campos de las categorías que cambiaron
                                            return {
                                                before: [],
                                                current: currentFiltered,
                                                hasChanges: currentFiltered.length > 0,
                                            };
                                        }

                                        // Encontramos campos que cambiaron o son nuevos (solo de las categorías que cambiaron)
                                        const changedFields = [];
                                        const changedBeforeFields = [];

                                        // Comparar campos actuales filtrados con los anteriores filtrados
                                        currentFiltered.forEach((currentItem) => {
                                            // Buscar si existe un campo igual en before
                                            const foundInBefore = beforeFiltered.find(
                                                (beforeItem) => beforeItem === currentItem
                                            );

                                            if (!foundInBefore) {
                                                // Es un campo nuevo o modificado
                                                changedFields.push(currentItem);

                                                // Buscar si hay un campo con el mismo identificador en before (para mostrar el cambio)
                                                // Usamos getFieldIdentifier para comparar el campo completo (ej: "CDA (Confidential Disclosure Agreement) (¿Enviado?)")
                                                // en lugar de solo el nombre del documento
                                                const currentFieldId = getFieldIdentifier(currentItem);
                                                const matchingBeforeField = beforeFiltered.find(
                                                    (beforeItem) => {
                                                        const beforeFieldId = getFieldIdentifier(beforeItem);
                                                        return beforeFieldId === currentFieldId;
                                                    }
                                                );

                                                if (matchingBeforeField) {
                                                    // Es una modificación de un campo existente
                                                    changedBeforeFields.push(matchingBeforeField);
                                                }
                                            }
                                        });

                                        // También verificar si hay campos en before que ya no están en current (eliminados)
                                        beforeFiltered.forEach((beforeItem) => {
                                            const foundInCurrent = currentFiltered.find(
                                                (currentItem) => currentItem === beforeItem
                                            );

                                            if (!foundInCurrent) {
                                                // El campo fue eliminado o modificado
                                                // Usamos getFieldIdentifier para comparar el campo completo
                                                const beforeFieldId = getFieldIdentifier(beforeItem);
                                                const matchingCurrentField = currentFiltered.find(
                                                    (currentItem) => {
                                                        const currentFieldId = getFieldIdentifier(currentItem);
                                                        return currentFieldId === beforeFieldId;
                                                    }
                                                );

                                                if (!matchingCurrentField) {
                                                    // El campo fue completamente eliminado
                                                    changedBeforeFields.push(beforeItem);
                                                }
                                            }
                                        });

                                        // Si hay cambios, mostrar los campos modificados
                                        if (
                                            changedFields.length > 0 ||
                                            changedBeforeFields.length > 0
                                        ) {
                                            return {
                                                before:
                                                    changedBeforeFields.length > 0
                                                        ? changedBeforeFields
                                                        : [],
                                                current: changedFields,
                                                hasChanges: true,
                                            };
                                        }

                                        // No hay cambios
                                        return {
                                            before: [],
                                            current: [],
                                            hasChanges: false,
                                        };
                                    };

                                    const result = filterAttachmentFieldValues(
                                        row.before,
                                        row.current
                                    );

                                    if (!result.hasChanges) {
                                        // no hay cambios, ignoro
                                        continue;
                                    }

                                    // registro solo lo cambiado
                                    row.before = result.before;
                                    row.current = result.current;

                                    // Establecer el tipo para renderizado
                                    row._t = "ArrayOfStrings";
                                } else {
                                    // Para Attachments, procesar formato "Categoría: NombreArchivo"
                                    if (propertyName.toLowerCase() === "attachments") {
                                        const processAttachments = (beforeArr, currentArr) => {
                                            if (!Array.isArray(beforeArr)) beforeArr = [];
                                            if (!Array.isArray(currentArr)) currentArr = [];

                                            // Función para extraer categoría y nombre de archivo del formato "Categoría: NombreArchivo"
                                            const parseAttachment = (item) => {
                                                if (typeof item !== "string")
                                                    return { category: item, fileName: item };
                                                const colonIndex = item.indexOf(":");
                                                if (colonIndex > 0) {
                                                    return {
                                                        category: item.substring(0, colonIndex).trim(),
                                                        fileName: item.substring(colonIndex + 1).trim(),
                                                    };
                                                }
                                                // Si no tiene formato "Categoría: NombreArchivo", asumir que es solo categoría (compatibilidad con datos antiguos)
                                                return { category: item, fileName: null };
                                            };

                                            // Parsear attachments
                                            const beforeParsed = beforeArr.map(parseAttachment);
                                            const currentParsed = currentArr.map(parseAttachment);

                                            // Agrupar por categoría para encontrar cambios
                                            const beforeByCategory = new Map();
                                            beforeParsed.forEach((item) => {
                                                if (!beforeByCategory.has(item.category)) {
                                                    beforeByCategory.set(item.category, []);
                                                }
                                                beforeByCategory.get(item.category).push(item.fileName);
                                            });

                                            const currentByCategory = new Map();
                                            currentParsed.forEach((item) => {
                                                if (!currentByCategory.has(item.category)) {
                                                    currentByCategory.set(item.category, []);
                                                }
                                                currentByCategory
                                                    .get(item.category)
                                                    .push(item.fileName);
                                            });

                                            // Encontrar categorías que cambiaron
                                            const allCategories = new Set([
                                                ...beforeByCategory.keys(),
                                                ...currentByCategory.keys(),
                                            ]);

                                            const changedAttachments = [];
                                            for (const category of allCategories) {
                                                const beforeFiles =
                                                    beforeByCategory.get(category) || [];
                                                const currentFiles =
                                                    currentByCategory.get(category) || [];

                                                // Obtener el último archivo de cada lado (versión más reciente)
                                                const beforeFile =
                                                    beforeFiles.length > 0
                                                        ? beforeFiles[beforeFiles.length - 1]
                                                        : null;
                                                const currentFile =
                                                    currentFiles.length > 0
                                                        ? currentFiles[currentFiles.length - 1]
                                                        : null;

                                                // Si hay cambio (archivo diferente o uno existe y el otro no)
                                                if (beforeFile !== currentFile) {
                                                    changedAttachments.push({
                                                        category,
                                                        beforeFile: beforeFile || null,
                                                        currentFile: currentFile || null,
                                                    });
                                                }
                                            }

                                            return changedAttachments;
                                        };

                                        const changedAttachments = processAttachments(
                                            row.before,
                                            row.current
                                        );

                                        if (changedAttachments.length === 0) {
                                            // No hay cambios, ignorar
                                            continue;
                                        }

                                        // Crear un renglón por cada archivo que cambió
                                        for (const attachment of changedAttachments) {
                                            const fileRow = {
                                                propertyName: attachment.category, // Categoría en CAMPOS
                                                before: attachment.beforeFile
                                                    ? [attachment.beforeFile]
                                                    : [], // Nombre del archivo anterior
                                                current: attachment.currentFile
                                                    ? [attachment.currentFile]
                                                    : [], // Nombre del archivo nuevo
                                                fieldName: attachment.category, // El nombre de la categoría es el campo
                                                _t: "arrayofstrings",
                                            };
                                            rows.push(fileRow);
                                        }
                                        // Continuar al siguiente propertyName sin procesar este row
                                        continue;
                                    } else if (
                                        propertyName.toLowerCase() === "keywords"
                                    ) {
                                        // Caso especial para subpatología (Keywords): selector multi-select.
                                        // Mostrar la lista COMPLETA en ANTES y AHORA para ver el estado en cada momento.
                                        const normalizeItem = (item) => {
                                            if (typeof item === "string") return item.trim();
                                            return item;
                                        };
                                        const beforeArr = Array.isArray(row.before)
                                            ? row.before.map(normalizeItem)
                                            : [];
                                        const currentArr = Array.isArray(row.current)
                                            ? row.current.map(normalizeItem)
                                            : [];

                                        const beforeSet = new Set(beforeArr);
                                        const currentSet = new Set(currentArr);
                                        const hasChanges =
                                            beforeArr.length !== currentArr.length ||
                                            beforeArr.some((x) => !currentSet.has(x)) ||
                                            currentArr.some((x) => !beforeSet.has(x));

                                        if (!hasChanges) continue;

                                        row.before = beforeArr;
                                        row.current = currentArr;
                                        row._t = "ArrayOfStrings";
                                        row.fieldName = _getFieldName(
                                            item.entityName,
                                            "Keywords"
                                        );
                                    } else {
                                        // Para otros arrays en startup (Documentos, etc.)
                                        const result = filterArrayDifferences(
                                            row.before,
                                            row.current
                                        );

                                        if (!result.hasChanges) {
                                            // Si no hay cambios, ignorar este campo
                                            continue;
                                        }

                                        // Actualizar con solo las diferencias
                                        row.before = result.before;
                                        row.current = result.current;
                                    }
                                }

                                // TODO: Esto es para mantener compatibilidad,
                                // pero hay que verificar por qué rompen la estructura
                                propertyName = propertyName.replace(/\[\d*\]/g, "");

                                // Si ya se estableció _t para AttachmentFieldValues, no sobrescribirlo
                                if (!row._t) {
                                    // _getLocaleSuffix("Startup") es "" → antes generaba ".startup.devices" (clave inválida).
                                    row.fieldName = _getFieldName(item.entityName, propertyName);

                                    switch (propertyName) {
                                        case "Attachments":
                                        case "Devices":
                                        case "AttachmentFieldValues":
                                        case "Keywords":
                                            row._t = "ArrayOfStrings";
                                            break;

                                        default:
                                            row._t = "Array";
                                            break;
                                    }
                                } else {
                                    // Si _t ya está establecido, solo establecer fieldName si no está establecido
                                    if (!row.fieldName) {
                                        row.fieldName = _getFieldName(item.entityName, propertyName);
                                    }
                                }
                            } else {
                                propertyName = propertyName.replace(/\[\d*\]/g, "");
                                row.fieldName = _getFieldName(item.entityName, propertyName);
                                row._t = _getFieldTypeFromName(propertyName);
                            }
                        }
                        break;
                    default:
                        {
                            if (Array.isArray(currentValues[propertyName])) {
                                row.fieldName = t(
                                    `${_getLocaleSuffix(item.entityName)}${_uncapitalize(
                                        propertyName
                                    )}`
                                ); //.title
                                row.localeSuffix = `${_getLocaleSuffix(
                                    item.entityName
                                )}${_uncapitalize(propertyName)}`;
                                row._t =
                                    propertyName === "StudyTasks" || propertyName === "Keywords"
                                        ? "ArrayOfStrings"
                                        : "Array";
                            } else {
                                propertyName = propertyName.replace(/\[\d*\]/g, "");
                                row.fieldName = _getFieldName(item.entityName, propertyName);
                                row._t = _getFieldTypeFromName(propertyName);
                            }
                        }
                        break;
                }
                rows.push(row);
            } catch (error) {
                console.log(error);
            }
        }


        return (
            <Panel key={`accordion-audit-item-${idx}`} eventKey={`accordion-audit-item-${idx}`}>
                <Panel.Heading>
                    <Panel.Title toggle>
                        <HighLight>{t("auditTrail.date")}</HighLight>: {dateTimeUTCFormatter(item.dateTime)} <HighLight>{t("auditTrail.action")}</HighLight>: {_getActionName(item.auditAction)} <HighLight>{t("auditTrail.user")}</HighLight>: {item.userId}</Panel.Title>
                </Panel.Heading>
                <Panel.Body collapsible>
                    {
                        rows.length > 0 &&
                        <div>
                            <Reason reason={item.reason} />
                            <Comments comments={item.comments} />
                            <Visit visitId={item.parentKey} />
                            <Table responsive key={`table-${idx}`}>
                                <thead>
                                    <tr>
                                        <th>{t("auditTrail.field")}</th>
                                        <th>{t("auditTrail.before")}</th>
                                        <th>{t("auditTrail.after")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        rows.map((row, idx) => {
                                            return (
                                                <tr key={`table-row-${idx}`}>
                                                    <td>{row.fieldName}</td>
                                                    <td>{_getValueFormatted(row, row.before, `before-${idx}`)}</td>
                                                    <td>{_getValueFormatted(row, row.current, `current-${idx}`)}</td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                            </Table>
                        </div>
                    }
                </Panel.Body>
            </Panel>
        );
    });

    return (
        <PanelGroup accordion id="accordion-audit-items">
            {auditItem}
        </PanelGroup>
    );
}

Audit.propTypes = {
    audit: PropTypes.array.isRequired,
    fields: PropTypes.array,
};